import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/Button';
import { testDatabaseConnection, testAuthConnection, testTableStructure } from '../utils/connectionTest';
import { supabase } from '../lib/supabase';
import { CheckCircle, XCircle, AlertTriangle, Database, Shield, Table, RefreshCw } from 'lucide-react';

export const ConnectionTest: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState<{
    database: { success: boolean; error?: string; needsMigration?: boolean } | null;
    auth: { success: boolean; error?: string } | null;
    tables: { success: boolean; tables?: Record<string, boolean>; error?: string } | null;
  }>({
    database: null,
    auth: null,
    tables: null
  });
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    runConnectionTests();
  }, []);

  const runConnectionTests = async () => {
    setTesting(true);
    
    console.log('🔍 Running comprehensive connection tests...');
    
    try {
      // Test database connection
      console.log('1. Testing database connection...');
      const dbResult = await testDatabaseConnection();
      setConnectionStatus(prev => ({ ...prev, database: dbResult }));

      // Test authentication system
      console.log('2. Testing authentication system...');
      const authResult = await testAuthConnection();
      setConnectionStatus(prev => ({ ...prev, auth: authResult }));

      // Test table structure
      console.log('3. Testing table structure...');
      const tablesResult = await testTableStructure();
      setConnectionStatus(prev => ({ ...prev, tables: tablesResult }));

    } catch (error) {
      console.error('Connection test failed:', error);
    } finally {
      setTesting(false);
    }
  };

  const getStatusIcon = (status: { success: boolean } | null) => {
    if (!status) return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    return status.success 
      ? <CheckCircle className="h-5 w-5 text-green-500" />
      : <XCircle className="h-5 w-5 text-red-500" />;
  };

  const getStatusText = (status: { success: boolean; error?: string } | null) => {
    if (!status) return 'Testing...';
    return status.success ? 'Connected' : `Failed: ${status.error}`;
  };

  const getStatusColor = (status: { success: boolean } | null) => {
    if (!status) return 'text-yellow-600 dark:text-yellow-400';
    return status.success 
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-600 dark:text-red-400';
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Supabase Database Connection Test
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Verify your Supabase connection and database schema
        </p>
      </div>

      {/* Configuration Status */}
      <div className="card p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Configuration Status
        </h2>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-700 dark:text-gray-300">Supabase URL:</span>
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
              <span className="text-green-600 dark:text-green-400">
                Configured
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-gray-700 dark:text-gray-300">Database Type:</span>
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
              <span className="text-green-600 dark:text-green-400">
                PostgreSQL (Supabase)
              </span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-gray-700 dark:text-gray-300">Overall Configuration:</span>
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
              <span className="text-green-600 dark:text-green-400">
                Ready
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Connection Tests */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center mb-4">
            <Database className="h-6 w-6 text-blue-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Database Connection
            </h3>
          </div>
          <div className="flex items-center justify-between">
            {getStatusIcon(connectionStatus.database)}
            <span className={`text-sm font-medium ${getStatusColor(connectionStatus.database)}`}>
              {getStatusText(connectionStatus.database)}
            </span>
          </div>
          {connectionStatus.database?.needsMigration && (
            <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                Database tables need to be created. Run the migration.
              </p>
            </div>
          )}
        </div>

        <div className="card p-6">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-green-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Authentication
            </h3>
          </div>
          <div className="flex items-center justify-between">
            {getStatusIcon(connectionStatus.auth)}
            <span className={`text-sm font-medium ${getStatusColor(connectionStatus.auth)}`}>
              {getStatusText(connectionStatus.auth)}
            </span>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center mb-4">
            <Table className="h-6 w-6 text-blue-500 mr-3" />
            <h3 className="text-lg font-semibent text-gray-900 dark:text-gray-100">
              Table Structure
            </h3>
          </div>
          <div className="flex items-center justify-between">
            {getStatusIcon(connectionStatus.tables)}
            <span className={`text-sm font-medium ${getStatusColor(connectionStatus.tables)}`}>
              {getStatusText(connectionStatus.tables)}
            </span>
          </div>
        </div>
      </div>

      {/* Detailed Results */}
      {connectionStatus.tables?.tables && (
        <div className="card p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Database Tables
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(connectionStatus.tables.tables).map(([table, exists]) => (
              <div key={table} className="flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                {exists ? (
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500 mr-2" />
                )}
                <span className={`text-sm font-medium ${exists ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {table}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-center">
        <Button
          onClick={runConnectionTests}
          variant="primary"
          loading={testing}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Run Tests Again
        </Button>
      </div>
    </div>
  );
};